import { useEffect, useState } from 'react'
import { Globe, Loader2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { api, getApiErrorMessage } from '@/lib/api'
import { toast } from 'sonner'

interface IPReputation {
  ip_address: string
  reputation_score: number
  blacklisted_count: number
  last_checked: string
}

// CHECKPOINT: PRD-09 FR-9.8 — IP reputation monitor for reserve IP pool
export function IPReputationTab() {
  const [ips, setIps] = useState<IPReputation[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchIPs = async () => {
      try {
        const res = await api.get('/deliverability/admin/reputation/ips')
        setIps(res.data.ips || [])
      } catch (err) {
        toast.error(getApiErrorMessage(err, 'Failed to load IP reputation'))
      } finally {
        setLoading(false)
      }
    }
    fetchIPs()
  }, [])

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <Globe className="h-4 w-4" />
          IP Reputation
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium">IP Address</th>
                <th className="px-4 py-3 text-left font-medium">Reputation Score</th>
                <th className="px-4 py-3 text-left font-medium">Blacklist Count</th>
                <th className="px-4 py-3 text-left font-medium">Last Checked</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center">
                    <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                  </td>
                </tr>
              ) : ips.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                    No IP reputation data
                  </td>
                </tr>
              ) : (
                ips.map((ip) => (
                  <tr key={ip.ip_address} className="border-b hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs">{ip.ip_address}</td>
                    <td className="px-4 py-3">
                      <span className={ip.reputation_score > 80 ? 'text-emerald-500' : ip.reputation_score > 50 ? 'text-amber-500' : 'text-red-500'}>
                        {ip.reputation_score}
                      </span>
                    </td>
                    <td className="px-4 py-3">{ip.blacklisted_count}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {new Date(ip.last_checked).toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}
